import { APIResponseStatus } from 'Services/api.types';

import { useServiceFeeNotice } from 'Components/transactionFees/hooks/useServiceFeeNotice';

import { usePaymentDetailsSub } from '../../hooks/usePaymentDetailsSub';
import { useSaleDetailsSub } from '../../hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../hooks/useVehicleCollectionSub';

import { type SaleDetailsContentViewProps, SaleDetailsContentView } from './components/SaleDetailsContentView/SaleDetailsContentView';
import { SaleDetailsContentProps } from './SaleDetails.types';

export const useSaleDetailsContent = ({
  showDetailsItems, title, titleIcon, variant,
}: SaleDetailsContentProps): SaleDetailsContentViewProps => {
  const { isSaleComplete } = useVehicleCollectionSub();
  const { paymentPageStatus } = usePaymentDetailsSub();

  const {
    enquiryId,
    financingAmount,
    hasNegativeEquity,
    isServiceFeeEnabled,
    neBankTransferTotalAmount,
    offerStatus,
    sellerAmount,
    serviceFee,
    serviceFeeTiers,
    soldForFigure,
    vehicleThumbnail,
    vrm,
  } = useSaleDetailsSub();

  const { feeAmountGBP } = useServiceFeeNotice({
    enquiryId,
    isServiceFeeEnabled,
    serviceFee,
    soldForFigure: soldForFigure ?? null,
    vrm,
  });

  const {
    showHomeCollectionLineItem = true,
    showPayoutDetails = false,
  } = showDetailsItems ?? {};

  const isPaymentCompletedUsingPay = paymentPageStatus === 'success';

  return {
    bankTransferFigure: neBankTransferTotalAmount,
    financeFigure: financingAmount,
    isPaymentCompletedUsingPay,
    isServiceFeeEnabled,
    mode: (() => {
      if (isSaleComplete) {
        return 'saleComplete';
      }

      if (hasNegativeEquity) {
        return 'negativeEquity';
      }

      return 'positiveEquity';
    })(),
    sellerFigure: sellerAmount,
    serviceFeeNoticeProps: {
      feeAmountGBP,
      serviceFeeTiers,
    },
    showAsError: offerStatus === APIResponseStatus.ERROR,
    showHomeCollectionLineItem,
    showOverpaymentMessage: !isSaleComplete,
    showPayoutDetails: showPayoutDetails || isServiceFeeEnabled,
    showVehicleCard: Boolean(vehicleThumbnail),
    soldForFigure,
    title,
    titleIcon,
    variant,
  };
};

export const SaleDetailsContent = (props: SaleDetailsContentProps) => (
  <SaleDetailsContentView {...useSaleDetailsContent(props)} />
);
